import {
    Button, Stack, Icon, Divider, Typography, Box,
    FormControlLabel, Checkbox
} from '@mui/material'


import { Fragment, useEffect, useState, useRef } from 'react'

import { useSelector } from 'react-redux'

import { useLandingZoneMigration } from '@features/hooks/LandingZones/migration'

import Link from '@components/Link'
import Alert from '@components/Alert'

import RequestHelp from '@views/Landingzones/Migration/Components/RequestHelp'

export const Inter = () => {
    const dataStore = useSelector(state => state.landingZoneMigration)

    const { getStatus, handleNext, handlePrevious } = useLandingZoneMigration()

    const [checked, setChecked] = useState([false, false])
    const [inProgress, setInProgress] = useState(false)
    const [hasDefaultRoutes, setHasDefaultRoutes] = useState(false)

    const timer = useRef()

    useEffect(() => {
        timer.current = setInterval(checkInterval, 10000)
        return () => { clearInterval(timer.current) }
    }, [])

    useEffect(() => {


        setInProgress(dataStore.selected.inProgress)
        setHasDefaultRoutes(dataStore.selected.defaultRoutes)
    }, [dataStore.selected])

    const checkInterval = () => {
        getStatus(dataStore.selected.lzId, false)
    }

    return (
        <Fragment>
            <Divider sx={{ mt: 2 }} />
            <Box sx={{ p: 2 }}>
                <Typography variant={'h5'} sx={{ mb: 1 }}>Inter Landing-Zone Migration</Typography>

                {(!inProgress) && (
                    <Fragment>

                        <Typography variant={'body1'} sx={{ mb: 2, lineHeight: 1.8 }}>
                            This migration step will redirect inter landing zones flows to the vWAN firewall, which means adding dedicated routes to the linked LZ IP ranges in your route table(s). Before migrating, check that your application is ready to migrate.
                        </Typography>

                        <Typography variant={'body1'} sx={{ mb: 2, lineHeight: 1.8 }}>
                            If your application is ready to migrate, you can also check the initial state of the following resources to make sure everything is normal:
                        </Typography>

                        <Box component="ul" sx={{ mb: 2, pl: 3 }}>
                            <li>
                                <Typography variant={'body2'} sx={{ mb: 1 }}>
                                    <strong>Landing zone default route table(s)</strong> <code>udr-iasp-&lt;region&gt;-lz-network</code> (if your LZ is on several regions, you will have one per region): you should notice that the cross region routes and the default routes point to the same IP (the regional Hub Firewall), and that "Propagate Gateway Routes" is set to "Yes" in the route table's configuration
                                </Typography>
                            </li>
                            <li>
                                <Typography variant={'body2'} sx={{ mb: 1 }}>
                                    <strong>Peering</strong> <code>peer-iasp-&lt;region&gt;-lz-network</code> of your virtual network(s) <code>vnet-iasp-&lt;region&gt;-lz-network</code>: you should see that the "Enable remote gateway or route server" parameter is activated
                                </Typography>
                            </li>
                        </Box>

                        <Typography variant={'body1'} sx={{ mb: 2, lineHeight: 1.8 }}>
                            Please also check the list of "linked Landing Zones" below. These Landing Zones have flows in common with your Landing Zone. The inter-lz migration will enable to route flows to the linked landing zones even if they still use the legacy firewall. If a linked Landing Zone is missing in the list below, you will lose the connection with it after migration while the linked Landing Zone is not migrated.
                        </Typography>
                        <Stack direction={'row'} alignItems={'center'} justifyContent={'start'} sx={{ mb: 2, gap: 1 }}>
                            <Typography variant={'body1'} sx={{ lineHeight: 1.8 }}>
                                For further explanation, to execute this step, please consult our documentation
                            </Typography>
                            <Link external target={'_blank'} href={'https://digitalplatforms.totalenergies.com/documentation/platforms/azure-platform/inoxscale-platform/user-communication/major-network-change#inter-landing-zones-migration'}>before migration</Link>

                        </Stack>

                        <Typography variant={'h6'} sx={{ fontWeight: 400, mb: 2 }}>
                            Please confirm the list of <strong>{dataStore.selected.interLzs?.length ?? 0}</strong> linked Landing Zones below before migrating
                        </Typography>

                        {(dataStore.selected.interLzs?.length > 0) && (
                            <Box sx={{ mb: 2 }}>
                                <Typography variant={'body1'} sx={{ fontWeight: 600, mb: 1, px: 1 }}>
                                    Linked Landing Zones ({dataStore.selected.interLzs?.length ?? 0})
                                </Typography>

                                <Box
                                    sx={{
                                        maxHeight: '236px',
                                        overflowY: 'auto',
                                        overflowX: 'hidden',
                                        display: 'grid',
                                        gridTemplateColumns: 'repeat(3, 1fr)',
                                        gap: '16px',
                                        padding: '12px',
                                        border: '1px solid #e0e0e0',
                                        borderRadius: '4px',
                                        backgroundColor: '#fafafa'
                                    }}
                                >
                                    {dataStore.selected.interLzs.map(item => (
                                        <Typography key={item} variant={'body2'} sx={{ mb: 0.5 }}>
                                            {item}
                                        </Typography>
                                    ))}
                                </Box>
                            </Box>
                        )}

                        <Stack sx={{ mb: 2 }}>
                            <FormControlLabel
                                control={<Checkbox checked={checked[0]} onChange={(e) => setChecked([e.target.checked, checked[1]])} />}
                                label={'I confirm the list of linked Landing Zone is correct.'}
                            />
                            <FormControlLabel
                                control={<Checkbox checked={checked[1]} onChange={(e) => setChecked([checked[0], e.target.checked])} />}
                                label={<Stack direction={'row'} alignItems={'center'}><span>I confirm my application is</span>&nbsp;<Link external target={'_blank'} href={'https://digitalplatforms.totalenergies.com/documentation/platforms/azure-platform/inoxscale-platform/user-communication/major-network-change#required-actions-to-prepare-migration'}>ready to migrate</Link></Stack>}
                            />
                        </Stack>

                        {hasDefaultRoutes && (
                            <Alert severity={'warning'} title={'Custom default routes detected.'} sx={{ mb: 2 }}>
                                <Stack direction={'row'} alignItems={'center'}>
                                    <span>For more information, go to the</span>&nbsp;
                                    <Link external target={'_blank'} href={'https://digitalplatforms.totalenergies.com/documentation/platforms/azure-platform/inoxscale-platform/user-communication/major-network-change'} noIcon>documentation</Link>
                                </Stack>
                            </Alert>
                        )}

                        <Alert severity={'warning'} title={'Prepare you to a short interruption of service.'} />
                    </Fragment>
                )
                }

                {inProgress && (<Alert severity={'info'} title={'Inter Landing-Zone migration is in progress'} />)}

                <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'} sx={{ mt: 2 }}>
                    <RequestHelp />
                    <Stack direction={'row'} alignItems={'center'} justifyContent={'end'} spacing={1}>
                        <Button variant={'secondary'} disabled startIcon={<Icon>undo</Icon>} onClick={handlePrevious}>Rollback</Button>
                        <Button startIcon={<Icon>sync_alt</Icon>} onClick={handleNext} disabled={(!(checked[0] && checked[1]) || hasDefaultRoutes || inProgress)}>Migrate</Button>
                    </Stack>
                </Stack>
            </Box >
        </Fragment >
    )
}

export default Inter